import React from 'react';
import styled from 'styled-components';
import { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import theme, { TOPIC_TO_COLOR } from '../../../theme';
import { CompanyResponse } from '../../../api/admin';
import {
  EditableLearningPlanItem,
  LearnerWithPlan,
} from '../Modals/EditLearnersLearningPlanModal';
import { SmallTooltip } from '../../Tooltip';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  components,
  MultiValueProps,
  MultiValueRemoveProps,
  GroupBase,
} from 'react-select';
import {
  StatusIconWrapper,
  getLearningPlanItemStyles,
  getUnitId,
  getStatusTooltip,
} from './LearningPlanCell';

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const selectCustomStyles: StylesConfig<EditableLearningPlanItem, true> = {
  menu: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.bgActive,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: '8px',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 3,
  }),
  container: (provided) => ({
    ...provided,
    margin: 0,
    marginTop: 0,
    paddingBottom: 0,
    width: '100%',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    flexWrap: 'nowrap',
    overflow: 'auto',
    width: '100%',
    scrollbarWidth: 'none',
  }),
  dropdownIndicator: (provided) => ({ ...provided, display: 'none' }),
  clearIndicator: (provided) => ({ ...provided, display: 'none' }),
  indicatorsContainer: (provided) => ({
    ...provided,
    display: 'none',
  }),
  multiValue: (provided, { data }) => ({
    ...provided,
    backgroundColor: TOPIC_TO_COLOR[data.topic as keyof typeof TOPIC_TO_COLOR],
    borderRadius: '200px',
    padding: '4px 8px',
    gap: '6px',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    width: 'max-content',
    minWidth: 'unset',
    transition: 'all 0.3s ease-in-out',
  }),
  multiValueLabel: (provided, { data }) => {
    const { tooltipId } = getLearningPlanItemStyles({
      item: data,
      learnerId: data.learnerId,
      theme,
    });
    return {
      ...provided,
      textOverflow: 'unset',
      width: '100%',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '15px',
      lineHeight: '20px',
      fontFeatureSettings: "'tnum' on, 'lnum' on",
      color: theme.colors.textActive,
      textAlign: 'center',
      padding: 0,
      margin: 0,
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
      '& > div > span': {
        dataTooltipId: tooltipId,
      },
    };
  },
  multiValueRemove: (provided) => ({
    ...provided,
    padding: 2,
    margin: 0,
    transition: 'all 0.3s ease-in-out',
  }),
  control: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    padding: 0,
    margin: 0,
    height: '100%',
    borderRadius: '8px',
    boxShadow: '0 !important',
    outline: 'none',
    '&:hover': {
      borderColor: 'transparent',
    },
    cursor: 'pointer',
  }),

  option: (
    provided,
    { isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    backgroundColor:
      isSelected || isFocused ? theme.colors.fieldBackground : 'transparent',
    color:
      isSelected || isFocused
        ? theme.colors.textPrimary
        : theme.colors.textRegular,
    cursor: isSelected ? 'not-allowed' : 'default',
    '&:active': {
      color: theme.colors.textPrimary,
      backgroundColor: theme.colors.fieldBackground,
    },
  }),

  singleValue: (provided) => ({
    ...provided,
    color: theme.colors.inputTextActive,
  }),
  input: (provided) => ({
    ...provided,
    color: theme.colors.inputTextActive,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: theme.colors.inputTextPlaceholder,
  }),
};

const SortableMultiValue = (
  props: MultiValueProps<
    EditableLearningPlanItem,
    true,
    GroupBase<EditableLearningPlanItem>
  >
) => {
  const { unitId } = getLearningPlanItemStyles({
    item: props.data,
    learnerId: props.data.learnerId,
    theme,
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: unitId,
  });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, 0px, 0)` // Only apply horizontal transform
      : undefined,
    transition,
    zIndex: isDragging ? 2 : undefined,
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseDown={onMouseDown}
    >
      <components.MultiValue {...props} />
    </div>
  );
};

const MultiValueRemove = (
  props: MultiValueRemoveProps<
    EditableLearningPlanItem,
    true,
    GroupBase<EditableLearningPlanItem>
  >
) => {
  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{
        onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
        ...props.innerProps,
      }}
    />
  );
};

const formatOptionLabel = (
  option: EditableLearningPlanItem,
  { context }: { context: 'menu' | 'value' },
  learnerId: number
) => {
  if (context === 'menu') {
    return option.label;
  }

  const { tooltipId, statusIconClass } = getLearningPlanItemStyles({
    item: option,
    learnerId,
    theme,
  });

  return (
    <span data-tooltip-id={tooltipId}>
      <StatusIconWrapper
        src={statusIconClass}
        color={theme.colors.textPrimary}
      />
      <span style={{ marginLeft: '8px' }}>{option.shortLabel}</span>
    </span>
  );
};

export default function LearningPlanEditableCell({
  learnerWithLearningPlan,
  options,
  onChange,
}: {
  learnerWithLearningPlan: LearnerWithPlan;
  onChange: (newLearningPlan: EditableLearningPlanItem[]) => void;
  options: EditableLearningPlanItem[];
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
}) {
  const getSortableItems = React.useCallback(() => {
    return learnerWithLearningPlan.newLearningPlan.map((planItem) =>
      getUnitId({
        item: planItem,
        learnerId: learnerWithLearningPlan.id,
      })
    );
  }, [learnerWithLearningPlan]);

  const onDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!active || !over) return;

      const items = learnerWithLearningPlan.newLearningPlan;
      const activeItemIndex = items.findIndex(
        (item) =>
          getUnitId({ item, learnerId: learnerWithLearningPlan.id }) ===
          active.id
      );
      const overItemIndex = items.findIndex(
        (item) =>
          getUnitId({ item, learnerId: learnerWithLearningPlan.id }) === over.id
      );

      if (
        activeItemIndex === -1 ||
        overItemIndex === -1 ||
        activeItemIndex === overItemIndex
      )
        return;

      const newItems = [...items];
      const [removed] = newItems.splice(activeItemIndex, 1);
      newItems.splice(overItemIndex, 0, removed);

      onChange(newItems);
    },
    [onChange, learnerWithLearningPlan]
  );

  const onValuesChange = React.useCallback(
    (newValue: readonly EditableLearningPlanItem[]) => {
      console.log('newValue', newValue);
      const newArray = [...newValue].map((item) => {
        const itemUnitId = getUnitId({
          item,
          learnerId: learnerWithLearningPlan.id,
        });

        const originalItem = learnerWithLearningPlan.originalLearningPlan.find(
          (original) =>
            getUnitId({
              item: original,
              learnerId: learnerWithLearningPlan.id,
            }) === itemUnitId
        );

        return originalItem || item;
      });

      onChange(newArray);
    },
    [
      onChange,
      learnerWithLearningPlan.originalLearningPlan,
      learnerWithLearningPlan.id,
    ]
  );

  return (
    <React.Fragment>
      <SelectWrapper>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
          modifiers={[restrictToHorizontalAxis]}
        >
          <SortableContext
            items={getSortableItems()}
            strategy={horizontalListSortingStrategy}
          >
            <CreatableSelect
              options={options}
              value={learnerWithLearningPlan.newLearningPlan}
              isMulti
              menuPortalTarget={document.body}
              placeholder="Add units to the learning plan.."
              isValidNewOption={() => false}
              styles={selectCustomStyles}
              formatOptionLabel={(option, context) =>
                formatOptionLabel(option, context, learnerWithLearningPlan.id)
              }
              onChange={onValuesChange}
              components={{
                MultiValue: SortableMultiValue,
                MultiValueRemove,
              }}
            />
          </SortableContext>
        </DndContext>
      </SelectWrapper>

      {learnerWithLearningPlan.newLearningPlan.map((item) => {
        const { tooltipId } = getLearningPlanItemStyles({
          item,
          learnerId: learnerWithLearningPlan.id,
          theme,
        });

        return (
          <SmallTooltip
            key={tooltipId}
            anchorSelect={`[data-tooltip-id="${tooltipId}"]`}
          >
            {getStatusTooltip({ item })}
          </SmallTooltip>
        );
      })}
    </React.Fragment>
  );
}
